import { useAnalytics } from '../../context/AnalyticsProvider';
import type { PaymentMethodType, UseCase } from '../../entities/enums';

import { errorMessages } from './constants';
import {
  PaymentCardUnsupportedError,
  PaymentSavedCreditCardPayError,
  PaymentConfirmationError,
  PaymentGeneralError,
  PaymentIntentExpire,
  CreditCardFormNotValidError,
  PaymentActionRequiredError,
  OrderError,
  PaymentDefaultError,
} from './customErrors';

type ErrorMessageKeys = keyof typeof errorMessages;
type ErrorMessageValues = (typeof errorMessages)[keyof typeof errorMessages];

export type HandleErrorType = (error: Error) => void;

export const useErrorHandler = ({
  paymentMethod,
  setErrorMessage,
  onPostAdButtonClickFailed,
  onError,
  useCase,
}: {
  paymentMethod: PaymentMethodType;
  setErrorMessage: React.Dispatch<React.SetStateAction<string | undefined>>;
  onPostAdButtonClickFailed: (error: Error, isPaymentError?: boolean) => void;
  onError: (error: Error, paymentMethod: PaymentMethodType) => void;
  useCase: UseCase;
}) => {
  const { sendViewAlertViewedEvent } = useAnalytics();

  let errorCode: ErrorMessageKeys;

  // get credit card error code
  const getErrorCode = (
    error:
      | PaymentCardUnsupportedError
      | PaymentSavedCreditCardPayError
      | PaymentConfirmationError,
    isSavedCreditCard?: boolean,
  ): ErrorMessageKeys => {
    // this logic covers when card is expired and saved credit card is used
    if (isSavedCreditCard && error?.declineCode === 'expired_card') {
      return 'saved_credit_card_expired';
    }
    if (error instanceof PaymentCardUnsupportedError && error.declineCode) {
      return error.declineCode as ErrorMessageKeys;
    }
    if (error instanceof PaymentConfirmationError) {
      if (error.declineCode) {
        return error.declineCode as ErrorMessageKeys;
      }

      if (error.errorCode) {
        return error.errorCode as ErrorMessageKeys;
      }

      return 'default_error';
    }
    if (error instanceof PaymentSavedCreditCardPayError) {
      if (error.errorCode) {
        return error.errorCode as ErrorMessageKeys;
      }

      if (error.declineCode) {
        return error.declineCode as ErrorMessageKeys;
      }

      return 'default_error';
    }

    return 'default_error';
  };

  const getErrorMessage = (errorKey: ErrorMessageKeys): ErrorMessageValues =>
    errorMessages[errorKey] ?? errorMessages.default_error;

  // Responsibility:
  // - set error message without translation
  // - send error event to analytics
  // - call onError callback
  // - call onPostAdButtonClickFailed callback
  const handleError: HandleErrorType = (error) => {
    switch (error.constructor) {
      case PaymentCardUnsupportedError:
      case PaymentConfirmationError:
        errorCode = getErrorCode(
          error as PaymentConfirmationError | PaymentCardUnsupportedError,
          false,
        );
        setErrorMessage(getErrorMessage(errorCode));
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: getErrorMessage(errorCode),
              messageCode: errorCode,
            },
          ],
          paymentMethod,
        );
        onPostAdButtonClickFailed(error, true);
        break;
      case PaymentSavedCreditCardPayError:
        errorCode = getErrorCode(error as PaymentSavedCreditCardPayError, true);
        setErrorMessage(getErrorMessage(errorCode));
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: getErrorMessage(errorCode),
              messageCode: errorCode,
            },
          ],
          paymentMethod,
        );
        onPostAdButtonClickFailed(error, true);
        break;
      case PaymentGeneralError:
        setErrorMessage(error.message);
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: error.message,
              messageCode: 'payment_general_error',
            },
          ],
          paymentMethod,
        );
        onPostAdButtonClickFailed(error, true);
        break;
      case PaymentDefaultError:
        setErrorMessage(error.message);
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title:
                "We couldn't process your payment Can you try a different payment method? Or reach out to our Customer Support team.",
              messageCode: 'payment_default_error',
            },
          ],
          paymentMethod,
        );

        onPostAdButtonClickFailed(error, true);
        break;
      case PaymentIntentExpire:
        setErrorMessage('qr.expire.error');
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: error.message,
              messageCode: 'purchase_qr_code_expired',
            },
          ],
          paymentMethod,
        );
        onPostAdButtonClickFailed(error, true);
        break;
      case CreditCardFormNotValidError:
      case PaymentActionRequiredError:
        // When credit card or any payment method form is not valid, we do not want to show any error message
        onPostAdButtonClickFailed(error, true);
        break;
      case OrderError:
        onError(error, paymentMethod);
        break;
      default:
        if (useCase === 'INVOICE') {
          setErrorMessage('order.error.payment.error');
        }
        onPostAdButtonClickFailed(error);
        onError(error, paymentMethod);
    }
  };

  return { handleError };
};

// TODO: Temporary function to fix the lint error. Got other card to work on disowned pay button
export const useOrderErrorHandler = ({
  paymentMethod,
  setErrorMessage,
  onError,
  useCase,
}: {
  paymentMethod: PaymentMethodType;
  setErrorMessage: React.Dispatch<React.SetStateAction<string | undefined>>;
  onError: (
    error: Error,
    paymentMethod: PaymentMethodType,
    isPaymentError?: boolean,
  ) => void;
  useCase: UseCase;
}) => {
  const { sendViewAlertViewedEvent } = useAnalytics();

  let errorCode: ErrorMessageKeys;

  // get credit card error code
  const getErrorCode = (
    error:
      | PaymentCardUnsupportedError
      | PaymentSavedCreditCardPayError
      | PaymentConfirmationError,
    isSavedCreditCard?: boolean,
  ): ErrorMessageKeys => {
    // this logic covers when card is expired and saved credit card is used
    if (isSavedCreditCard && error?.declineCode === 'expired_card') {
      return 'saved_credit_card_expired';
    }
    if (error instanceof PaymentCardUnsupportedError && error.declineCode) {
      return error.declineCode as ErrorMessageKeys;
    }
    if (error instanceof PaymentConfirmationError) {
      if (error.declineCode) {
        return error.declineCode as ErrorMessageKeys;
      }

      if (error.errorCode) {
        return error.errorCode as ErrorMessageKeys;
      }

      return 'default_error';
    }
    if (error instanceof PaymentSavedCreditCardPayError) {
      if (error.errorCode) {
        return error.errorCode as ErrorMessageKeys;
      }

      if (error.declineCode) {
        return error.declineCode as ErrorMessageKeys;
      }

      return 'default_error';
    }

    return 'default_error';
  };

  const getErrorMessage = (errorKey: ErrorMessageKeys): ErrorMessageValues =>
    errorMessages[errorKey] ?? errorMessages.default_error;

  // Responsibility:
  // - set error message without translation
  // - send error event to analytics
  // - call onError callback
  const handleError: HandleErrorType = (error) => {
    switch (error.constructor) {
      case PaymentCardUnsupportedError:
      case PaymentConfirmationError:
        errorCode = getErrorCode(
          error as PaymentConfirmationError | PaymentCardUnsupportedError,
          false,
        );
        setErrorMessage(getErrorMessage(errorCode));
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: getErrorMessage(errorCode),
              messageCode: errorCode,
            },
          ],
          paymentMethod,
        );
        onError(error, paymentMethod, true);
        break;
      case PaymentSavedCreditCardPayError:
        errorCode = getErrorCode(error as PaymentSavedCreditCardPayError, true);
        setErrorMessage(getErrorMessage(errorCode));
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: getErrorMessage(errorCode),
              messageCode: errorCode,
            },
          ],
          paymentMethod,
        );
        onError(error, paymentMethod, true);
        break;
      case PaymentGeneralError:
        setErrorMessage(error.message);
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: error.message,
              messageCode: 'payment_general_error',
            },
          ],
          paymentMethod,
        );
        onError(error, paymentMethod, true);
        break;
      case PaymentDefaultError:
        setErrorMessage(error.message);
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title:
                "We couldn't process your payment Can you try a different payment method? Or reach out to our Customer Support team.",
              messageCode: 'payment_default_error',
            },
          ],
          paymentMethod,
        );
        onError(error, paymentMethod, true);
        break;
      case PaymentIntentExpire:
        setErrorMessage('qr.expire.error');
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: error.message,
              messageCode: 'purchase_qr_code_expired',
            },
          ],
          paymentMethod,
        );
        onError(error, paymentMethod, true);
        break;
      case CreditCardFormNotValidError:
      case PaymentActionRequiredError:
        // When credit card or any payment method form is not valid, we do not want to show any error message
        onError(error, paymentMethod, true);
        break;
      case OrderError:
        onError(error, paymentMethod);
        break;
      default:
        if (useCase === 'INVOICE') {
          setErrorMessage('order.error.payment.error');
        }
        onError(error, paymentMethod);
    }
  };

  return { handleError };
};
